import React, { useMemo } from 'react';
import {
  AttackPathLookupProvider,
  buildAttackPathLookupFromToolResults,
} from './AttackPathLookupContext';
import {
  FactCheckLookupProvider,
  buildFactCheckLookupFromToolResults,
} from './FactCheckLookupContext';
import { ToolResultDataContext } from './ToolResultDataContext';

type ContentBlock = Array<{
  type: string;
  toolName?: string;
  toolUseId?: string;
  content?: unknown;
}>;

interface MessageContentLookupProviderProps {
  content: ContentBlock;
  children: React.ReactNode;
}

/**
 * Single provider that builds and supplies attack path, fact-check, and
 * tool result data lookups from message content (tool results).
 *
 * The ToolResultDataContext maps toolName → raw JSON content so that
 * markdown renderers (e.g. MarkdownFindings, MarkdownAssets) can merge
 * the full tool result data (which includes resourceTags, asset info, etc.)
 * back into the large language model simplified fenced-block payload.
 */
export const MessageContentLookupProvider: React.FC<
  MessageContentLookupProviderProps
> = ({ content, children }) => {
  const attackPathLookup = useMemo(
    () => buildAttackPathLookupFromToolResults(content),
    [content],
  );

  const factCheckLookup = useMemo(
    () => buildFactCheckLookupFromToolResults(content),
    [content],
  );

  const toolResultDataMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const block of content) {
      if (block.type === 'toolResult' && block.content != null) {
        const serialized =
          typeof block.content === 'string'
            ? block.content
            : JSON.stringify(block.content);
        // Map by toolName for backward-compat (single-use tools like RenderHTML)
        if (block.toolName) {
          map[block.toolName] = serialized;
        }
        // Also map by toolUseId so multi-call tools (shellRun etc.)
        // can look up their own result precisely.
        if (block.toolUseId) {
          map[block.toolUseId] = serialized;
        }
      }
    }
    return map;
  }, [content]);

  return (
    <ToolResultDataContext.Provider value={toolResultDataMap}>
      <AttackPathLookupProvider lookup={attackPathLookup}>
        <FactCheckLookupProvider lookup={factCheckLookup}>
          {children}
        </FactCheckLookupProvider>
      </AttackPathLookupProvider>
    </ToolResultDataContext.Provider>
  );
};
